import React, { useState } from "react";
import { Collapse, IconButton } from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import InfiniteScroll from "react-infinite-scroll-component";

import { getTranslateByKey, isNullUndefined } from "@/main/services/utils";
import { getManagePackagesStatus } from "@/main/services/data/data";
import { userIsBodyshop, userIsInsurer } from "@/main/services/utilsUser";

import { RoutesConstants } from "@/main/routes/routesConstants";

import { GetPackageManagementResponse, IGetPackageDetailsResponse } from "@/main/models/response";

import { packingTrackingApi } from "@/main/api";

import LoadingComponent from "@/main/components/shared/loading/loadingComponent";
import ColumnLabel from "@/main/components/common/columnLabel/columnLabel";
import ResultNotFoundComponent from "@/main/components/shared/resultNotFound/resultNotFoundComponent";
import StatusColorManagerPackages from "@/main/components/shared/status/statusColorManagerPackages";

import InfoOrder from "../infoOrder/infoOrder";
import * as T from "./types";
import * as S from "./styles";

interface IContentCardProps {
	each: GetPackageManagementResponse;
	trackingCode: number;
}

const ContentCard = ({ each, trackingCode }: IContentCardProps) => {
	const [expanded, setExpanded] = useState(false);
	const [dataManagePackages, setDataManagePackages] = useState<IGetPackageDetailsResponse | null>(null);
	const [isLoadingOrder, setIsLoadingOrder] = useState(false);

	async function loadViewPackageDetails(guidPackingTracking: string | undefined) {
		try {
			setIsLoadingOrder(true);

			const {
				data: { result },
			} = await packingTrackingApi.getPackageDetails(String(guidPackingTracking));
			setDataManagePackages(result);
		} catch {
		} finally {
			setIsLoadingOrder(false);
		}
	}

	// Handle Functions
	function handleClickRow(e: React.MouseEvent<any>) {
		e.stopPropagation();
		setExpanded(!expanded);
		if (dataManagePackages === null) loadViewPackageDetails(each?.guidPackingTracking);
	}
	function renderText(text: string | number | undefined, hasTooltip: boolean = false, titleColumn?: string) {
		if (!text) return "-";

		if (hasTooltip) {
			return (
				<S.TooltipText title={!isNullUndefined(titleColumn) ? String(titleColumn) : String(text)}>
					<div
						style={{
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						{String(text)}
					</div>
				</S.TooltipText>
			);
		}
		return String(text);
	}

	// CONSTANTS LAYOUT
	const showColumnBodyshop = !userIsBodyshop();
	const showColumnInsurer = !userIsInsurer();
	const hideColumn = !showColumnInsurer || !showColumnBodyshop;

	function findPackagesStatusName(idStatus: number) {
		let statusName: string = "";
		getManagePackagesStatus().forEach((status) => {
			if (status.key === idStatus) {
				statusName = status.value;
			}
		});
		return statusName;
	}

	return (
		<>
			<S.ContentContainer>
				<S.Content
					id={`id-searchManagePackages-row-order-${each.guidPackingTracking}`}
					onClick={handleClickRow}
					hideColumn={hideColumn}
				>
					<ColumnLabel
						label={getTranslateByKey("information.order")}
						content={
							<>
								<S.HyperLink
									href={`/#${RoutesConstants.orderSearchOrder}?quoteFilterVehicle=${each.procurementCode}&startDate=${each.procurementDate}&finalDate=${each.procurementDate}`}
									target="_blank"
									onClick={(e) => e.stopPropagation()}
								>
									{renderText(each.procurementCode, true, getTranslateByKey("information.viewOrder"))}
								</S.HyperLink>
							</>
						}
					/>
					<ColumnLabel
						label={getTranslateByKey("information.claimNumber")}
						content={renderText(each.claimNumber, true)}
					/>
					<ColumnLabel label={getTranslateByKey("information.vehicle")} content={renderText(each.vehicle, true)} />
					{showColumnInsurer && (
						<ColumnLabel
							label={getTranslateByKey("information.insurer")}
							content={renderText(each.insurerName, true)}
						/>
					)}
					{showColumnBodyshop && (
						<ColumnLabel
							label={getTranslateByKey("information.bodyshop")}
							content={renderText(each.bodyShopName, true)}
						/>
					)}

					<ColumnLabel
						label={getTranslateByKey("packingTrackingHistory.trackingNumber")}
						content={renderText(each.trackingCode, true)}
					/>
					<ColumnLabel
						label={getTranslateByKey("information.ShippingCompany")}
						content={renderText(each.shippingCompanyName, true)}
					/>
					<ColumnLabel
						label=""
						content={
							<StatusColorManagerPackages
								status={{
									id: Number(each.packageStatus),
									name: findPackagesStatusName(each.packageStatus),
								}}
							/>
						}
						propsLabel={{ style: { textAlign: "center" } }}
					/>
					<S.AreaIcon>
						<IconButton
							onClick={handleClickRow}
							id={`id-searchManagePackages-button-colapse-${each.guidPackingTracking}`}
						>
							{expanded === false ? (
								<ExpandMore aria-expanded={expanded} aria-label={getTranslateByKey("information.showMore")} />
							) : (
								<ExpandLess aria-expanded={expanded} aria-label={getTranslateByKey("information.showMore")} />
							)}
						</IconButton>
					</S.AreaIcon>
				</S.Content>
			</S.ContentContainer>
			{each.guidPackingTracking && (
				<Collapse in={expanded} timeout="auto" unmountOnExit>
					<S.ContentCollapse>
						<>
							<InfoOrder
								guidCompany={each.guidPackingTracking}
								dataManagePackages={dataManagePackages}
								isLoadingOrder={isLoadingOrder}
								trackingCode={trackingCode}
								isManagePackages
							/>
						</>
					</S.ContentCollapse>
				</Collapse>
			)}
		</>
	);
};

export default function CardGridOrder({ title, gridData, formFilters, infiniteScrollConfig }: T.ICardGridProps) {
	function renderGrid() {
		return (
			<S.ContainerCard>
				{gridData.data.map((each) => (
					<ContentCard key={each.guidPackingTracking} each={each} trackingCode={each.trackingCode} />
				))}
			</S.ContainerCard>
		);
	}
	if (gridData.isLoading) return <LoadingComponent />;
	if ((!gridData.data || gridData.data.length === 0) && formFilters !== null)
		return <ResultNotFoundComponent styles={{ marginTop: 10 }} />;
	if (gridData.data.length === 0 && !gridData.isLoading) return null;
	return (
		<S.Container>
			{title && <S.Title>{title}</S.Title>}

			{infiniteScrollConfig ? (
				<div style={{ minHeight: "95vh" }}>
					<InfiniteScroll
						style={{ overflowX: "hidden" }}
						dataLength={infiniteScrollConfig.dataLength}
						next={infiniteScrollConfig.fetchData}
						hasMore={infiniteScrollConfig.hasMore}
						loader={infiniteScrollConfig.isLoading ? <LoadingComponent /> : <></>}
						endMessage={
							<S.EndMessage className="d-flex d-flex justify-content-center">
								{getTranslateByKey("information.allResultsWereDisplayed")}
							</S.EndMessage>
						}
					>
						{renderGrid()}
					</InfiniteScroll>
				</div>
			) : (
				renderGrid()
			)}
		</S.Container>
	);
}
